import { useState, useMemo } from "react";
import type {
	BundleRecommendationResponse,
	RecommendationItem,
} from "../types/viewmodel";
import styles from "./RecommendationsList.module.css";
import Drawer from "./common/Drawer";
import RecommendationsPrice from "./common/RecommendationsPrice";
import { addProductToCart, updateSmallCart } from "../services/cartService";
import { customGaEvent } from "../utils/customGA";
import { useViewModel, useIsB2B } from "../hooks/useViewModel.ts";
import { useAccessoriesPrices } from "../hooks/useAccessoriesPrices.ts";

const ACCESSORY_LIST_ID = "kpc_config_accessories";

const toCamelCase = (value: string) =>
	value.charAt(0).toLowerCase() + value.slice(1);

interface RecommendationsListProps {
	recommendations?: BundleRecommendationResponse;
	isLoading?: boolean;
	error?: Error | null;
}

const RecommendationsList: React.FC<RecommendationsListProps> = ({
	recommendations,
	isLoading,
	error,
}) => {
	const [openGroup, setOpenGroup] = useState<string | null>(null);
	const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

	const { data } = useViewModel();
	const { strings } = data!;
	const isB2B = useIsB2B();
	const bundleId = data?.viewModel?.id ?? "";

	const allAccessoryIds = useMemo(
		() => recommendations?.placements?.[0]?.items?.map((i) => i.id) ?? [],
		[recommendations],
	);

	const { data: priceMap, isLoading: isPricesLoading } = useAccessoriesPrices({
		bundleId,
		targetProductIds: allAccessoryIds,
	});

	const resolveItemPrice = (item: RecommendationItem) => {
		const accessoryPrice = priceMap?.[item.id];
		if (!accessoryPrice) {
			return {
				savings: item.priceBefore - item.price,
				priceBefore: item.priceBefore,
				resolvedPrice: item.price,
			};
		}

		const hasDiscount = !!accessoryPrice.bestDiscount;
		const discountedNet = accessoryPrice.bestDiscount?.discountedPriceNet ?? accessoryPrice.listPriceNet;
		const discountedGross = accessoryPrice.bestDiscount?.discountedPriceGross ?? accessoryPrice.listPriceGross;
		const currentPrice = isB2B ? discountedNet : discountedGross;
		const priceBefore = hasDiscount
			? (isB2B ? accessoryPrice.listPriceNet : accessoryPrice.listPriceGross)
			: undefined;
		const savings = hasDiscount
			? (isB2B
				? (accessoryPrice.bestDiscount?.netDiscount ?? 0)
				: (accessoryPrice.bestDiscount?.grossDiscount ?? 0))
			: undefined;

		return {
			savings,
			priceBefore,
			resolvedPrice: currentPrice,
		};
	};

	if (isLoading && !strings) {
		return null;
	}

	if (error || !recommendations?.placements?.length) {
		return <div className={styles.empty}>No recommendations available</div>;
	}

	if (allAccessoryIds.length > 0 && isPricesLoading) {
		return null;
	}

	const groups: Record<string, RecommendationItem[]> = {};
	for (const item of recommendations.placements[0].items) {
		const key =
			item.bundleGroup ??
			strings?.product.configBundles.accessories.defaultGroupName;
		if (!groups[key]) {
			groups[key] = [];
		}

		groups[key].push(item);
	}

	const handleOpenGroup = (groupName: string) => {
		setOpenGroup(groupName);

		customGaEvent({
			event: "view_item_list",
			item_list_name: groupName,
			item_list_id: ACCESSORY_LIST_ID,
			ecommerce: {
				items: groups[groupName].map((item, index) => ({
					item_id: item.id,
					item_name: item.name,
					price: resolveItemPrice(item).resolvedPrice,
					item_list_name: groupName,
					item_list_id: ACCESSORY_LIST_ID,
					index,
					quantity: 1,
				})),
			},
			_clear: true,
		});
	};

	const handleAddToCart = async ({
		item,
		groupName,
		index,
	}: {
		item: RecommendationItem;
		groupName: string;
		index: number;
	}) => {
		customGaEvent({
			event: "add_to_cart",
			ecommerce: {
				items: [
					{
						item_id: item.id,
						item_name: item.name,
						price: resolveItemPrice(item).resolvedPrice,
						item_list_name: groupName,
						item_list_id: ACCESSORY_LIST_ID,
						index,
						quantity: 1,
					},
				],
			},
			_clear: true,
		});

		try {
			const data = await addProductToCart(item.id);
			updateSmallCart(data);
			setAddedItems((prev) => new Set(prev).add(item.id));
			await new Promise<void>((resolve) => setTimeout(resolve, 2000));
			setAddedItems((prev) => {
				const next = new Set(prev);
				next.delete(item.id);
				return next;
			});
		} catch (err) {
			console.error("Error adding product to cart:", err);
		}
	};

	return (
		<div>
			<h2 className={styles.acessoriesHeader}>
				{strings?.product.configBundles.accessories.pcAccessories}
			</h2>
		{Object.entries(groups).map(([groupName, items]) => {
			const resolvedFeatured = resolveItemPrice(items[0]);
			return (
			<div key={groupName} className={styles.placement}>
					<ul className={styles.grid} role="list">
						{items[0] && (
							<li key={items[0].id}>
								<article className={styles.item}>
									<figure className={styles.itemLeft}>
										{items[0].imageURL && (
											<img
												src={items[0].imageURL}
												alt={items[0].imageAltText || items[0].name}
												className={styles.itemImage}
											/>
										)}
									</figure>
									<div className={styles.itemMiddle}>
										<span className={styles.itemCategory}>
											{items[0].bundleGroup
												? (strings?.product.configBundles.accessories?.[
														toCamelCase(items[0].bundleGroup)
													] ?? items[0].bundleGroup)
												: undefined}
										</span>
										<h3 className={styles.itemTitle}>{items[0].name}</h3>
										<p className={styles.itemDescription}>
											{items[0].description}
										</p>
									</div>
							<div className={styles.itemRight}>
									<RecommendationsPrice
										price={resolvedFeatured.resolvedPrice}
										monthlyPriceFormatted={items[0].monthlyPriceFormatted}
										savings={resolvedFeatured.savings}
										priceBefore={resolvedFeatured.priceBefore}
									/>
										<div className={styles.itemActions}>
											<button
												type="button"
												className={styles.buttonOutline}
												onClick={() => handleOpenGroup(groupName)}
											>
												{strings?.product.configBundles.seeMore}
											</button>
											{addedItems.has(items[0].id) ? (
												<button
													type="button"
													className={styles.successButton}
													disabled
												>
													<svg
														className={styles.checkmark}
														viewBox="0 0 52 52"
														aria-hidden="true"
													>
														<circle
															className={styles["checkmark__circle"]}
															cx="26"
															cy="26"
															r="25"
															fill="none"
														/>
														<path
															className={styles["checkmark__check"]}
															fill="none"
															d="M14.1 27.2l7.1 7.2 16.7-16.8"
														/>
													</svg>
													{
														strings?.product.configBundles.accessories
															.addedToCart
													}
												</button>
											) : (
												<button
													type="button"
													className={styles.buttonPrimary}
													onClick={() =>
														handleAddToCart({
															item: items[0],
															groupName,
															index: 0,
														})
													}
												>
													{strings?.product.configBundles.add}
												</button>
											)}
										</div>
									</div>
								</article>
							</li>
						)}
					</ul>
				</div>
			);
		})}

			<Drawer
				isOpen={!!openGroup}
				onClose={() => setOpenGroup(null)}
				title={openGroup ?? ""}
			>
				{openGroup && groups[openGroup] && (
					<ul className={styles.grid} role="list">
					{groups[openGroup].map((item, index) => {
						const resolved = resolveItemPrice(item);
						return (
						<li key={item.id}>
								<article
									className={`${styles.item} ${index === 0 ? styles.itemHighlighted : ""}`}
								>
									<figure className={styles.itemLeft}>
										{item.imageURL && (
											<img
												src={item.imageURL}
												alt={item.imageAltText || item.name}
												className={styles.itemImage}
											/>
										)}
									</figure>
									<div className={styles.itemMiddle}>
										<span className={styles.itemCategory}>
											{item.bundleGroup
												? (strings?.product.configBundles.accessories?.[
														toCamelCase(item.bundleGroup)
													] ?? item.bundleGroup)
												: undefined}
										</span>
										<h3 className={styles.itemTitle}>{item.name}</h3>
										<p className={styles.itemDescription}>{item.description}</p>
									</div>
								<div className={styles.itemRight}>
								<RecommendationsPrice
									price={resolved.resolvedPrice}
									monthlyPriceFormatted={item.monthlyPriceFormatted}
									savings={resolved.savings}
									priceBefore={resolved.priceBefore}
								/>
										<div className={styles.itemActions}>
											{addedItems.has(item.id) ? (
												<button
													type="button"
													className={styles.successButton}
													disabled
												>
													<svg
														className={styles.checkmark}
														viewBox="0 0 52 52"
														aria-hidden="true"
													>
														<circle
															className={styles["checkmark__circle"]}
															cx="26"
															cy="26"
															r="25"
															fill="none"
														/>
														<path
															className={styles["checkmark__check"]}
															fill="none"
															d="M14.1 27.2l7.1 7.2 16.7-16.8"
														/>
													</svg>
													{
														strings?.product.configBundles.accessories
															.addedToCart
													}
												</button>
											) : (
												<button
													type="button"
													className={styles.buttonPrimary}
													onClick={() =>
														handleAddToCart({
															item,
															groupName: openGroup,
															index,
														})
													}
												>
													{strings?.product.configBundles.add}
												</button>
											)}
										</div>
									</div>
								</article>
						</li>
					);
					})}
				</ul>
				)}
			</Drawer>
		</div>
	);
};

export default RecommendationsList;